/**
 * The coloured box, as a real node.
 *
 * IT NEVER WORKED. The toolbar inserted a box by handing the editor a literal
 * `<div class="callout callout--warn"><p>…</p></div>`, and nothing in the schema
 * described a `div`. ProseMirror parses HTML against the schema it has, so the
 * wrapper was dropped on the way in and the paragraph was inserted bare - the
 * writer pressed 「注意」, saw their text appear with no box around it, and had no
 * way to tell whether the control was broken or they had used it wrong. The same
 * happened on load: a stored box came back from Markdown as a div, hit
 * `setContent`, and unwrapped itself. A box could not be created and could not
 * survive; only the export path, which rebuilds boxes from the stored text
 * without going through the editor, ever showed one.
 *
 * So it is a node now, with content of its own - which is also what makes it
 * behave: the text inside is real editable content, Enter makes a second
 * paragraph inside the box, and the caret can leave it.
 *
 * WRAPPING, NOT INSERTING. `insertContent` put a new box with placeholder text
 * below whatever you had selected, so making a paragraph into a warning meant
 * inserting a box, retyping the paragraph into it and deleting the original.
 * `toggleWrap` puts the box around what is already there, and pressing the same
 * colour again takes it off.
 *
 * THE COLOUR IS NOT LIMITED TO FOUR. The four presets are the ones a clinic
 * reaches for by name - 補足, 注意, ポイント, NG - and they stay as classes so
 * they keep meaning something. Anything else is a hex on the node, published as
 * `--callout`, and the stylesheet mixes the fill from it. One value rather than
 * a pair, because a border and a background chosen independently is how a
 * document ends up with a red box on a green field.
 *
 * ROUND TRIP. The rendered element is `div.callout`, which is exactly what
 * rich-text.ts already carries through Markdown verbatim, and what the export
 * rebuilds from an allowlist. A custom colour is a `--callout` declaration in
 * the style attribute - re-read here by parseHTML, and re-validated as a hex on
 * the server before it reaches any published file.
 */
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { blockStyle } from '../theme.ts';

/** The named boxes. Anything else is a colour, not a kind. */
export const CALLOUT_KINDS = ['info', 'warn', 'note', 'stop'] as const;
export type CalloutKind = (typeof CALLOUT_KINDS)[number] | 'custom';

/** Six-digit hex only. The one shape allowed to reach a style attribute. */
export const isHexColor = (value: string): boolean => /^#[0-9a-fA-F]{6}$/.test(value);

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: {
      /** Wrap the selection in a box, or unwrap it if it is already this one. */
      toggleCallout: (attributes: { kind: CalloutKind; color?: string | null }) => ReturnType;
      /** Take the box off, keeping the text. */
      unsetCallout: () => ReturnType;
    };
  }
}

export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  /*
   * Block content, not inline: a box holds paragraphs, lists and even a table -
   * a clinic's 注意 box is regularly three bullet points. `defining` keeps the
   * box when its contents are replaced, so selecting everything inside and
   * pasting does not silently delete the box around it.
   */
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      kind: {
        default: 'info' as CalloutKind,
        parseHTML: (element) => /callout--([a-z]+)/.exec(element.className)?.[1] ?? 'info',
        renderHTML: (attributes) => ({
          class: `callout callout--${String(attributes.kind)}`,
        }),
      },
      color: {
        default: null as string | null,
        // Read back from the custom property rather than from a colour
        // declaration, so a paste carrying its own background cannot decide how
        // this box looks - only `--callout` is ours.
        parseHTML: (element) => {
          const value = element.style.getPropertyValue('--callout').trim();
          return isHexColor(value) ? value : null;
        },
        renderHTML: (attributes) => {
          const color = attributes.color;
          return typeof color === 'string' && isHexColor(color)
            ? { style: `--callout: ${color}` }
            : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.callout' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      toggleCallout:
        (attributes) =>
        ({ chain, editor }) => {
          /*
           * Same box pressed twice is "take it off".
           *
           * Compared on the pair, because 「注意」 and a custom orange are two
           * different boxes even when both are wrapping the same paragraph, and
           * pressing one while inside the other should change the colour rather
           * than unwrap and leave the text bare.
           */
          const current = editor.getAttributes('callout');
          const same =
            editor.isActive('callout') &&
            current.kind === attributes.kind &&
            (current.color ?? null) === (attributes.color ?? null);
          if (same) return chain().focus().lift('callout').run();
          // Already in a box of another colour: repaint it rather than nest one.
          if (editor.isActive('callout')) {
            return chain().focus().updateAttributes('callout', attributes).run();
          }
          return chain().focus().wrapIn('callout', attributes).run();
        },
      unsetCallout:
        () =>
        ({ chain }) =>
          chain().focus().lift('callout').run(),
    };
  },
});

/**
 * The other two structured blocks: the supervisor credit and the CTA.
 *
 * Same problem as the coloured box had, and the same fix. `insert-markdown.ts`
 * builds these as `<div class="bio">` and `<div class="ctabox">`, and a div that
 * no node describes is dropped by ProseMirror on the way in - so a writer would
 * press 挿入 and watch the doctor's card arrive as four bare paragraphs with the
 * photograph missing.
 *
 * One node with a `variant` rather than two nearly identical ones: they differ
 * only in the class they carry and the CSS that reads it. Not editable content
 * by accident either - the text inside stays real block content, so a writer can
 * fix a typo in the credentials without going back to the catalog.
 */
export const DocBlock = Node.create({
  name: 'docBlock',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: 'bio',
        parseHTML: (element) => (element.classList.contains('ctabox') ? 'ctabox' : 'bio'),
        renderHTML: (attributes) => ({ class: String(attributes.variant) }),
      },
      /*
       * This block's own colour, or null to follow the clinic's theme.
       *
       * Stored as ONE value, `--block`, and read back from nothing else - the
       * same rule the coloured box follows with `--callout` - so a paste that
       * carries its own background cannot decide how the block looks. What is
       * rendered is that value plus the colours derived from it (button text,
       * title, tint, border: shared/theme.ts blockColors), so the stylesheet
       * never has to compute a contrast.
       */
      color: {
        default: null as string | null,
        parseHTML: (element) => {
          const value = element.style.getPropertyValue('--block').trim();
          return isHexColor(value) ? value.toLowerCase() : null;
        },
        renderHTML: (attributes) => {
          const color = attributes.color;
          return typeof color === 'string' && isHexColor(color) ? { style: blockStyle(color) } : {};
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.bio' }, { tag: 'div.ctabox' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes), 0];
  },
});

/**
 * The classes inside a structured block, kept.
 *
 * A schema drops what it does not describe, and ProseMirror's paragraph
 * describes no class - so `<p class="bio__name">山田 太郎</p>` came back as a bare
 * paragraph. On screen that made the credit card four identical grey lines
 * instead of a label, a name and its credentials; on SAVE it was worse, because
 * the editor's HTML is what gets written back, so opening an article and
 * pressing save quietly flattened a block that had been correct in storage.
 *
 * An allowlist, not a passthrough. These are the classes this application's own
 * blocks are built from (insert-markdown.ts); a class arriving from a paste is
 * somebody else's stylesheet and is dropped like any other unknown markup. The
 * published file does not trust this either - it rebuilds each block from
 * scratch against the same names.
 */
const BLOCK_CLASSES = [
  'bio__photo',
  // The stand-in drawn when a supervisor has no photograph: the initial in a
  // circle, as a paragraph, because there is no image to hang it on.
  'bio__blank',
  'bio__label',
  'bio__name',
  'bio__meta',
  'bio__profile',
  'ctabox__title',
  'ctabox__channels',
];

export const BlockClass = Extension.create({
  name: 'blockClass',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'image'],
        attributes: {
          blockClass: {
            default: null,
            parseHTML: (element: HTMLElement) => {
              const classes = (element.getAttribute('class') ?? '').split(/\s+/);
              return BLOCK_CLASSES.find((name) => classes.includes(name)) ?? null;
            },
            renderHTML: (attributes: Record<string, unknown>) =>
              typeof attributes.blockClass === 'string' ? { class: attributes.blockClass } : {},
          },
        },
      },
    ];
  },
});
